import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import API from '../services/api';

const ResetPassword = () => {
    // Grab the ?token= from the URL
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [passwords, setPasswords] = useState({ password: '', confirmPassword: '' });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (passwords.password !== passwords.confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (!token) {
            setError("Invalid or missing reset token.");
            return;
        }

        setIsLoading(true);
        setMessage('');
        setError('');

        try {
            // This endpoint will need to be created in your PHP backend
            const res = await API.post('/process_reset.php', { 
                token: token, 
                new_password: passwords.password 
            });
            
            if (res.status === 200) {
                setMessage("Password reset successfully! Redirecting to login...");
                setTimeout(() => navigate('/login'), 3000); // Send to login after 3 seconds
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to reset password. Link may be expired.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-wrapper">
            <style>{`
                /* Re-using the exact same CSS as above to keep it perfectly uniform */
                .login-wrapper { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(-45deg, #134628, #0a2415, #1b6339, #134628); background-size: 400% 400%; animation: gradientBG 15s ease infinite; padding: 120px 20px 60px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
                .login-box { background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(20px); width: 100%; max-width: 450px; border-radius: 24px; box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5); overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.2); animation: slideUpFade 0.8s cubic-bezier(0.2, 0.8, 0.2, 1); }
                .login-header-section { background: linear-gradient(135deg, #134628 0%, #1c663a 100%); color: white; padding: 50px 20px 40px; text-align: center; position: relative; }
                .login-header-section::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 5px; background: linear-gradient(90deg, #ffc300, #ffb703); }
                .login-logo-circle { width: 80px; height: 80px; background: linear-gradient(135deg, #ffc300 0%, #ffb703 100%); color: #134628; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; font-size: 2.2rem; box-shadow: 0 10px 25px rgba(255, 195, 0, 0.4); animation: pulseGlow 2s infinite; }
                .login-header-section h2 { font-weight: 900; letter-spacing: 2px; margin: 0 0 5px 0; font-size: 1.8rem; }
                .login-header-section p { color: #a0c4ff; margin: 0; font-size: 1rem; font-weight: 300; }
                .login-form { padding: 40px 35px 30px; }
                .input-group-custom { margin-bottom: 25px; position: relative; }
                .input-group-custom label { display: block; margin-bottom: 10px; font-weight: 700; color: #134628; font-size: 0.95rem; }
                .input-wrapper { position: relative; display: flex; align-items: center; }
                .input-wrapper i { position: absolute; left: 15px; color: #a0aab5; font-size: 1.1rem; transition: color 0.3s ease; }
                .input-group-custom input { width: 100%; padding: 14px 15px 14px 45px; border: 2px solid #e1e4e8; border-radius: 12px; font-size: 1rem; background: #fdfdfd; color: #333; transition: all 0.3s ease; }
                .input-group-custom input:focus { border-color: #ffc300; outline: none; background: #ffffff; box-shadow: 0 0 0 4px rgba(255, 195, 0, 0.15); }
                .input-group-custom input:focus + i, .input-wrapper:focus-within i { color: #ffc300; }
                .login-submit-btn { width: 100%; background: linear-gradient(45deg, #ffc300, #ffb703); color: #134628; border: none; padding: 16px; border-radius: 12px; font-weight: 800; font-size: 1.1rem; cursor: pointer; transition: all 0.4s ease; text-transform: uppercase; letter-spacing: 1px; margin-top: 5px; box-shadow: 0 8px 20px rgba(255, 195, 0, 0.3); }
                .login-submit-btn:hover { transform: translateY(-3px); box-shadow: 0 15px 30px rgba(255, 195, 0, 0.5); background: linear-gradient(45deg, #ffb703, #ffc300); }
                .login-footer { text-align: center; padding: 0 30px 40px; font-size: 0.95rem; color: #666; }
                .login-footer a { color: #134628; font-weight: 800; margin-left: 8px; text-decoration: none; transition: color 0.3s; border-bottom: 2px solid transparent; }
                .login-footer a:hover { color: #ffc300; border-bottom-color: #ffc300; }
                .alert-msg { padding: 12px; border-radius: 8px; margin-bottom: 20px; font-weight: bold; text-align: center; font-size: 0.9rem; }
                .alert-success { background: #e6f4ea; color: #134628; border: 1px solid #cce8d6; }
                .alert-error { background: #fce8e6; color: #d93025; border: 1px solid #fad2cf; }
            `}</style>

            <div className="login-box">
                <div className="login-header-section">
                    <div className="login-logo-circle">
                        <i className="fas fa-unlock-alt"></i>
                    </div>
                    <h2>NEW PASSWORD</h2>
                    <p>Create a secure new password</p>
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                    {message && <div className="alert-msg alert-success">{message}</div>}
                    {error && <div className="alert-msg alert-error">{error}</div>}

                    {!token && !error && !message && (
                        <div className="alert-msg alert-error">
                            No reset token found in URL. Please use the link sent to your email.
                        </div>
                    )}

                    <div className="input-group-custom">
                        <label htmlFor="password">New Password</label>
                        <div className="input-wrapper">
                            <i className="fas fa-lock"></i>
                            <input 
                                id="password"
                                type="password" 
                                name="password" 
                                placeholder="New Password"
                                value={passwords.password}
                                onChange={handleChange} 
                                required 
                            />
                        </div>
                    </div>

                    <div className="input-group-custom">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <div className="input-wrapper">
                            <i className="fas fa-check-circle"></i>
                            <input 
                                id="confirmPassword"
                                type="password" 
                                name="confirmPassword" 
                                placeholder="Confirm New Password"
                                value={passwords.confirmPassword}
                                onChange={handleChange} 
                                required 
                            />
                        </div>
                    </div>

                    <button type="submit" className="login-submit-btn" disabled={isLoading || !token}>
                        {isLoading ? 'Updating...' : 'Update Password'} <i className="fas fa-save ms-2"></i>
                    </button>
                </form>
                
                <div className="login-footer">
                    <Link to="/login">Back to Login</Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;